
import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop} from 'vue-property-decorator';
import {EOrderStatuses, IOrder} from '@/model/Order/orderInterfaces';

interface IStatusStep {
  key: string;
  label: string;
  stepState: 'done' | 'active' | 'pending';
}

// Linear happy-path used only for the visual step indicator. Several statuses collapse
// into the same node (e.g. all "waiting to be collected" variants) since the stepper is
// a decorative progress display, not a full state-machine model.
// labelKey points at the existing per-status short labels (orders.order.statusesShort.*)
// rather than introducing a new set of translation keys — one representative status per node.
const STEP_DEFINITIONS: {key: string; labelKey: string; statuses: string[]}[] = [
  {key: 'new', labelKey: 'orders.order.statusesShort.status_order_new', statuses: [EOrderStatuses.status_order_new]},
  {key: 'pickup', labelKey: 'orders.order.statusesShort.status_order_awaiting_collect', statuses: [
    EOrderStatuses.status_order_awaiting_collect,
    EOrderStatuses.status_order_awaiting_customer_dropoff,
    EOrderStatuses.status_order_in_route_collect,
  ]},
  {key: 'collected', labelKey: 'orders.order.statusesShort.status_order_sp_collected', statuses: [EOrderStatuses.status_order_sp_collected]},
  {key: 'assembled', labelKey: 'orders.order.statusesShort.status_order_assembled', statuses: [EOrderStatuses.status_order_assembled]},
  {key: 'delivery', labelKey: 'orders.order.statusesShort.status_order_in_route_delivery', statuses: [EOrderStatuses.status_order_in_route_delivery]},
  {key: 'delivered', labelKey: 'orders.order.statusesShort.status_order_sp_delivered', statuses: [EOrderStatuses.status_order_sp_delivered]},
  {key: 'completed', labelKey: 'orders.order.statusesShort.status_order_completed', statuses: [EOrderStatuses.status_order_completed]},
];

// Mirrors OrderStatusChip's color-per-status map, scoped to the statuses the stepper covers.
const STEP_STATUS_COLOR: Record<string, string> = {
  status_order_new: 'light-blue',
  status_order_awaiting_collect: 'indigo',
  status_awaiting_customer_dropoff: 'pink',
  status_order_in_route_collect: 'blue-grey lighten-1',
  status_order_sp_collected: 'purple',
  status_order_assembled: 'amber darken-2',
  status_order_in_route_delivery: 'blue-grey',
  status_order_sp_delivered: 'pink',
  status_order_completed: 'green',
};

const STEPPER_EXCLUDED_STATUSES = [
  EOrderStatuses.status_order_sp_cancelled,
  EOrderStatuses.status_order_client_cancelled,
  EOrderStatuses.status_order_sp_rejected,
  EOrderStatuses.status_order_delayed,
  EOrderStatuses.status_order_professional_service_search,
  EOrderStatuses.status_order_professional_service_confirmed,
];

@Component({name: 'OrderStatusStepper'})
export default class OrderStatusStepper extends Vue {

  @Prop()
  protected order!: IOrder;

  protected get showStepper(): boolean {
    return !STEPPER_EXCLUDED_STATUSES.includes(this.order.status);
  }
  protected get stepActiveColor(): string {
    return STEP_STATUS_COLOR[this.order.status] || 'primary';
  }
  protected get statusSteps(): IStatusStep[] {
    const currentIndex = STEP_DEFINITIONS.findIndex((def) => def.statuses.includes(this.order.status));
    return STEP_DEFINITIONS.map((def, i) => ({
      key: def.key,
      label: String(this.$t(def.labelKey)),
      stepState: currentIndex === -1 ? 'pending' : (i < currentIndex ? 'done' : i === currentIndex ? 'active' : 'pending'),
    }));
  }
}
